import axios from 'axios';

const API = axios.create({
  baseURL: import.meta.env.VITE_INVESTMENTS_API_URL || 'http://localhost:5001',
  headers: { 'Content-Type': 'application/json' },
  timeout: 12000,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Metas con Proposito — "Tu dinero ahorra contigo"
export async function previewGoal({ targetAmountCop, targetDate, currentCop = 0 }) {
  const { data } = await API.post('/api/goals/preview', { targetAmountCop, targetDate, currentCop });
  return data?.data || null;
}

export async function createGoal({ name, targetAmountCop, targetDate, icon = null, monthlyTargetCop }) {
  const { data } = await API.post('/api/goals', { name, targetAmountCop, targetDate, icon, monthlyTargetCop });
  return data?.data || null;
}

export async function listGoals() {
  const { data } = await API.get('/api/goals');
  return data?.data || [];
}

export async function getGoal(id) {
  const { data } = await API.get(`/api/goals/${id}`);
  return data?.data || null;
}

export async function addContribution(id, { amountCop, note = null }) {
  const { data } = await API.post(`/api/goals/${id}/contributions`, { amountCop, note });
  return data || null; // { data: goal, newlyReached: [...] }
}

export async function updateGoal(id, patch) {
  const { data } = await API.patch(`/api/goals/${id}`, patch);
  return data?.data || null;
}

export async function archiveGoal(id) {
  const { data } = await API.delete(`/api/goals/${id}`);
  return data?.success || false;
}

// Reinicia la meta a cero (borra aportes/progreso, conserva nombre/monto/fecha).
export async function resetGoal(id) {
  const { data } = await API.post(`/api/goals/${id}/reset`);
  return data?.data || null;
}

// Genera el audio de Daniela con el resumen de metas y lo envia al WhatsApp del cliente.
export async function sendGoalsVoice(phone) {
  const { data } = await API.post('/api/goals/whatsapp-voice', { phone }, { timeout: 75000 });
  return data; // { mode: 'voice'|'text_link', audioUrl }
}

// Genera el audio del resumen de metas para ESCUCHARLO en la app (sin WhatsApp).
export async function generateGoalsVoiceAudio() {
  const { data } = await API.post('/api/goals/voice-audio', {}, { timeout: 75000 });
  return data; // { audioUrl }
}

// Narra el diagnostico financiero con voz (el `result` se calcula en el cliente).
export async function generateDiagnosticVoice(result) {
  const { data } = await API.post('/api/goals/diagnostic-voice', { result }, { timeout: 75000 });
  return data; // { audioUrl }
}

// El Oraculo proactivo: sugerencia de reequilibrio entre metas (si la hay).
export async function getReallocationSuggestion() {
  const { data } = await API.get('/api/goals/reallocation/suggestion');
  return data?.data || { hayOportunidad: false };
}

// Aplica la reasignacion de un toque (sin SMS — no sale dinero, es contable).
export async function applyReallocation({ fromGoalId, toGoalId, amountCop }) {
  const { data } = await API.post('/api/goals/reallocation/apply', { fromGoalId, toGoalId, amountCop }, { timeout: 20000 });
  return data;
}

// Macro -> meta: como la inflacion (Progresion) impacta cada meta.
export async function getMacroImpact() {
  const { data } = await API.get('/api/goals/macro-impact');
  return data?.data || null;
}

// Saldo libre -> metas: sugerencia de como repartir el cash quieto.
export async function getDistributionSuggestion() {
  const { data } = await API.get('/api/goals/distribution/suggestion');
  return data?.data || null;
}

// El "Mapa de tu dinero" (Gemelo Financiero): grafo completo + proximo mejor paso.
export async function getFinancialMap() {
  const { data } = await API.get('/api/goals/financial-map');
  return data?.data || null;
}

// Narra el Mapa de tu dinero con voz (Daniela) para escucharlo en la app.
export async function generateMapVoiceAudio() {
  const { data } = await API.post('/api/goals/map-voice-audio', {}, { timeout: 75000 });
  return data; // { audioUrl }
}

// Envia el audio del Mapa al WhatsApp del cliente.
export async function sendMapWhatsapp(phone) {
  const { data } = await API.post('/api/goals/map-whatsapp', { phone }, { timeout: 75000 });
  return data;
}

// Rentabilidad historica real de los portafolios (+ tasa conservadora de planeacion).
export async function getPortfolioReturns() {
  const { data } = await API.get('/api/goals/portfolios');
  return data || { data: [], disclaimer: '' };
}

// Vista previa de como se invertiria un aporte (opcion B: dirigido a rebalanceo). NO ejecuta.
export async function investPreview(id, amountCop) {
  const { data } = await API.post(`/api/goals/${id}/invest/preview`, { amountCop });
  return data?.data || null;
}

// Prepara la inversion del aporte y envia el codigo SMS.
export async function investPrepare(id, amountCop, phone) {
  const { data } = await API.post(`/api/goals/${id}/invest/prepare`, { amountCop, phone }, { timeout: 20000 });
  return data; // { token, data: planSummary, smsSent }
}

// Verifica el codigo y ejecuta la inversion (cobra comision + compra).
export async function investConfirm(id, token, code, phone) {
  const { data } = await API.post(`/api/goals/${id}/invest/confirm`, { token, code, phone }, { timeout: 40000 });
  return data; // { data: goal, newlyReached, execution }
}

const PORTFOLIO_LABEL_INV = {
  Guardian: 'Guardian', Explorer: 'Explorador', Visionary: 'Visionario', 'Income Builder': 'Constructor de Ingresos',
};
export function fmtUsd2(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---- Helpers de formato (pesos) ----
export function fmtCop(n) {
  return '$' + Math.round(Number(n || 0)).toLocaleString('es-CO');
}

export function fmtCopShort(n) {
  const v = Math.round(Number(n || 0));
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (v >= 1_000) return '$' + Math.round(v / 1_000) + 'k';
  return '$' + v.toLocaleString('es-CO');
}

export function fmtDateLong(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
}

const HORIZON_LABEL = { corto: 'Corto plazo', mediano: 'Mediano plazo', largo: 'Largo plazo' };
export function horizonLabel(h) {
  return HORIZON_LABEL[h] || h;
}

const PORTFOLIO_LABEL = {
  Guardian: 'Guardian', Explorer: 'Explorador', Visionary: 'Visionario', 'Income Builder': 'Constructor de Ingresos',
};
export function portfolioLabel(p) {
  return PORTFOLIO_LABEL[p] || p;
}
